/**
 * Drafting a journal (M03-04) or the opening balances (M03-08). Totals update
 * as you type, with the difference shown until the entry balances. Opening
 * balances do not need to balance: the difference goes to Opening Balance
 * Suspense and stays flagged until cleared.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Decimal, amount, dec, parseAmount } from '../../lib/money';
import { manilaToday, useAccounts, useDimensions } from '../../lib/queries';
import { AccountSelect } from '../../components/AccountSelect';
import { Alert, Button, Card, Field, Money, PageHeader, Skeleton, filterClass, inputClass } from '../../components/ui';
import type { JournalDetail } from './journalTypes';

interface DraftLine {
  key: number;
  accountId: string;
  debit: string;
  credit: string;
  description: string;
  dimensions: Record<string, string>;
}

let nextKey = 1;
const blank = (): DraftLine => ({ key: nextKey++, accountId: '', debit: '', credit: '', description: '', dimensions: {} });

export function JournalEditorPage({ mode = 'journal' }: { mode?: 'journal' | 'opening' }): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accounts = useAccounts();
  const dimensions = useDimensions();
  const existing = useQuery({
    queryKey: ['journal', id],
    queryFn: () => api<JournalDetail>(`/journals/${id}`),
    enabled: Boolean(id),
  });

  const [date, setDate] = useState(manilaToday());
  const [narration, setNarration] = useState('');
  const [reference, setReference] = useState('');
  const [autoReverse, setAutoReverse] = useState('');
  const [lines, setLines] = useState<DraftLine[]>(() => [blank(), blank()]);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const e = existing.data;
    if (!e) return;
    setDate(e.date);
    setNarration(e.narration);
    setReference(e.reference);
    setAutoReverse(e.autoReverseDate ?? '');
    setLines(
      e.lines.map((l) => ({
        key: nextKey++,
        accountId: l.accountId,
        debit: dec(l.debit).isZero() ? '' : dec(l.debit).toFixed(2),
        credit: dec(l.credit).isZero() ? '' : dec(l.credit).toFixed(2),
        description: l.description,
        dimensions: l.dimensions,
      })),
    );
  }, [existing.data]);

  const activeDimensions = (dimensions.data ?? []).filter((d) => d.active && d.values.some((v) => v.active));

  const totals = useMemo(() => {
    let dr = new Decimal(0);
    let cr = new Decimal(0);
    for (const l of lines) {
      dr = dr.plus(dec(parseAmount(l.debit) ?? 0));
      cr = cr.plus(dec(parseAmount(l.credit) ?? 0));
    }
    return { dr, cr, diff: dr.minus(cr) };
  }, [lines]);

  const update = (key: number, patch: Partial<DraftLine>): void =>
    setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const payload = () => ({
    date,
    narration: mode === 'opening' ? undefined : narration,
    reference: mode === 'opening' ? undefined : reference,
    autoReverseDate: mode === 'opening' ? undefined : autoReverse || null,
    ...(existing.data ? { rowVersion: existing.data.rowVersion } : {}),
    lines: lines
      .filter((l) => l.accountId && (parseAmount(l.debit) || parseAmount(l.credit)))
      .map((l) => ({
        accountId: l.accountId,
        debit: parseAmount(l.debit) ?? undefined,
        credit: parseAmount(l.credit) ?? undefined,
        description: l.description || undefined,
        dimensions: Object.fromEntries(Object.entries(l.dimensions).filter(([, v]) => v)),
      })),
  });

  const save = useMutation({
    mutationFn: async (then: 'stay' | 'post') => {
      const body = payload();
      let entry: JournalDetail;
      if (mode === 'opening') entry = await api<JournalDetail>('/journals/opening-balances', { method: 'POST', body });
      else if (id) entry = await api<JournalDetail>(`/journals/${id}`, { method: 'PATCH', body });
      else entry = await api<JournalDetail>('/journals', { method: 'POST', body });
      if (then === 'post') entry = await api<JournalDetail>(`/journals/${entry.id}/post`, { method: 'POST' });
      return entry;
    },
    onSuccess: (entry) => {
      void queryClient.invalidateQueries({ queryKey: ['journals'] });
      void queryClient.invalidateQueries({ queryKey: ['journal', entry.id] });
      navigate(`/journals/${entry.id}`, { replace: true });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'Could not save.')),
  });

  if (id && existing.isLoading) return <Skeleton className="h-96 w-full" />;
  if (existing.data && existing.data.status !== 'DRAFT') {
    return <Alert tone="warning">Only drafts can be edited. <Link className="underline" to={`/journals/${id}`}>Back to the entry</Link></Alert>;
  }

  const title = mode === 'opening' ? 'Opening Balances' : id ? `Edit draft` : 'New Journal Entry';
  const balanced = totals.diff.isZero() && totals.dr.gt(0);

  return (
    <>
      <PageHeader
        title={title}
        description={
          mode === 'opening'
            ? 'Enter each account’s balance on the start date. Any difference goes to Opening Balance Suspense until you clear it. Receivables and payables come in with their open invoices and bills, not here.'
            : 'Drafts get their number when they are posted. Entries above your approval limit go to an approver.'
        }
      />
      {error && (
        <div className="mb-4">
          <Alert tone="critical">
            {error.message}
            {error.fieldMessages.length > 1 && (
              <ul className="mt-1 list-disc pl-5">
                {error.fieldMessages.slice(1).map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            )}
          </Alert>
        </div>
      )}
      <Card className="mb-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={mode === 'opening' ? 'Balances as of' : 'Date'} required error={error?.fields.date}>
            <input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          {mode === 'journal' && (
            <>
              <Field label="Reference">
                <input className={inputClass} value={reference} maxLength={100} onChange={(e) => setReference(e.target.value)} />
              </Field>
              <Field label="Reverse automatically on" hint="For accruals. Optional." error={error?.fields.autoReverseDate}>
                <input type="date" className={inputClass} value={autoReverse} onChange={(e) => setAutoReverse(e.target.value)} />
              </Field>
              <div className="sm:col-span-2 lg:col-span-4">
                <Field label="Narration">
                  <textarea className={inputClass} rows={2} maxLength={500} value={narration} onChange={(e) => setNarration(e.target.value)} />
                </Field>
              </div>
            </>
          )}
        </div>
      </Card>

      <Card title="Lines">
        {accounts.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="w-8 pb-2">#</th>
                  <th className="pb-2 pr-2">Account</th>
                  <th className="pb-2 pr-2">Description</th>
                  {activeDimensions.map((d) => (
                    <th key={d.id} className="pb-2 pr-2">{d.name}</th>
                  ))}
                  <th className="w-36 pb-2 pr-2 text-right">Debit</th>
                  <th className="w-36 pb-2 pr-2 text-right">Credit</th>
                  <th className="w-10 pb-2" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, index) => (
                  <tr key={l.key} className="align-top">
                    <td className="py-1.5 pr-2 text-slate-400">{index + 1}</td>
                    <td className="py-1.5 pr-2">
                      <AccountSelect
                        accounts={accounts.data ?? []}
                        value={l.accountId}
                        onChange={(accountId) => update(l.key, { accountId })}
                        className={`${filterClass} w-full`}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input className={`${filterClass} w-full`} value={l.description} maxLength={300} onChange={(e) => update(l.key, { description: e.target.value })} aria-label={`Line ${index + 1} description`} />
                    </td>
                    {activeDimensions.map((d) => (
                      <td key={d.id} className="py-1.5 pr-2">
                        <select
                          className={`${filterClass} w-full`}
                          value={l.dimensions[d.id] ?? ''}
                          aria-label={`Line ${index + 1} ${d.name}`}
                          onChange={(e) => update(l.key, { dimensions: { ...l.dimensions, [d.id]: e.target.value } })}
                        >
                          <option value="">—</option>
                          {d.values.filter((v) => v.active).map((v) => (
                            <option key={v.id} value={v.id}>{v.name}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                    <td className="py-1.5 pr-2">
                      <input
                        className={`${filterClass} w-full text-right tabular-nums`}
                        inputMode="decimal"
                        value={l.debit}
                        aria-label={`Line ${index + 1} debit`}
                        onChange={(e) => update(l.key, { debit: e.target.value, credit: e.target.value ? '' : l.credit })}
                        onBlur={() => { const v = parseAmount(l.debit); if (v) update(l.key, { debit: v }); }}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        className={`${filterClass} w-full text-right tabular-nums`}
                        inputMode="decimal"
                        value={l.credit}
                        aria-label={`Line ${index + 1} credit`}
                        onChange={(e) => update(l.key, { credit: e.target.value, debit: e.target.value ? '' : l.debit })}
                        onBlur={() => { const v = parseAmount(l.credit); if (v) update(l.key, { credit: v }); }}
                      />
                    </td>
                    <td className="py-1.5">
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 dark:hover:bg-red-950"
                        aria-label={`Remove line ${index + 1}`}
                        disabled={lines.length <= (mode === 'opening' ? 1 : 2)}
                        onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))}
                      >
                        <Trash2 aria-hidden="true" className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 font-semibold dark:border-slate-800">
                  <td colSpan={3 + activeDimensions.length} className="pt-3">
                    <Button type="button" size="sm" variant="secondary" onClick={() => setLines((all) => [...all, blank()])}>
                      <Plus aria-hidden="true" className="h-4 w-4" /> Add line
                    </Button>
                  </td>
                  <td className="pt-3 pr-2 text-right"><Money>{amount(totals.dr.toString())}</Money></td>
                  <td className="pt-3 pr-2 text-right"><Money>{amount(totals.cr.toString())}</Money></td>
                  <td />
                </tr>
                {!totals.diff.isZero() && (
                  <tr>
                    <td colSpan={3 + activeDimensions.length} />
                    <td colSpan={2} className="pt-1 pr-2 text-right text-sm font-medium text-amber-700 dark:text-amber-300">
                      {mode === 'opening' ? 'To suspense' : 'Out of balance by'} <Money>{amount(totals.diff.abs().toString())}</Money>
                    </td>
                    <td />
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="secondary" busy={save.isPending && save.variables === 'stay'} disabled={mode === 'journal' && !balanced} onClick={() => { setError(null); save.mutate('stay'); }}>
          Save draft
        </Button>
        {mode === 'journal' && (
          <Button busy={save.isPending && save.variables === 'post'} disabled={!balanced} onClick={() => { setError(null); save.mutate('post'); }}>
            Save and post
          </Button>
        )}
      </div>
    </>
  );
}
